import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  SparklesIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { CallLeadInfo } from '../../services/agentService';

interface ScriptAssistPopupProps {
  isActive: boolean;
  leadInfo: CallLeadInfo | null;
  callStatus?: 'loading' | 'connecting' | 'ready' | 'ringing' | 'in-call' | 'error';
}

interface StageChoice {
  id: string;
  label: string;
  nextStageId: string;
  color?: string;
}

interface Stage {
  id: string;
  title: string;
  body: string;
  choices: StageChoice[];
  notes?: {
    goal: string;
    avoid: string;
    handle: string;
    psychology: string;
  };
}

interface RuntimeRule {
  id: string;
  event: 'connected' | 'stage_enter' | 'button_click';
  sourceStageId?: string;
  buttonLabel?: string;
  action: 'go_to_stage' | 'open_objections' | 'restart_script';
  targetStageId?: string;
  enabled?: boolean;
}

interface Objection {
  id: string;
  objection: string;
  response: string;
}

const CHOICE_COLORS: Record<string, string> = {
  green: 'bg-emerald-600 hover:bg-emerald-500 text-white',
  blue: 'bg-blue-600 hover:bg-blue-500 text-white',
  amber: 'bg-amber-600 hover:bg-amber-500 text-white',
  red: 'bg-red-600 hover:bg-red-500 text-white',
  slate: 'bg-slate-600 hover:bg-slate-500 text-white',
};
const DEFAULT_CHOICE_COLOR = 'bg-slate-700 hover:bg-slate-600 text-white';

const normalizeArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.map((item) => String(item || '').trim()).filter(Boolean) : [];

const bulletLines = (value: string): string[] =>
  String(value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

function buildDefaultChoices(
  currentId: string,
  allStageIds: string[],
  isObjectionsAvailable: boolean
): StageChoice[] {
  const idx = allStageIds.indexOf(currentId);
  const choices: StageChoice[] = [];
  if (idx < allStageIds.length - 1) {
    choices.push({
      id: `next-${currentId}`,
      label: 'Continue →',
      nextStageId: allStageIds[idx + 1],
      color: 'green',
    });
  }
  if (isObjectionsAvailable) {
    choices.push({
      id: `obj-${currentId}`,
      label: 'Handle Objection',
      nextStageId: '__objections__',
      color: 'amber',
    });
  }
  return choices;
}

const ScriptAssistPopup: React.FC<ScriptAssistPopupProps> = ({ isActive, leadInfo, callStatus }) => {
  const [minimized, setMinimized] = useState(false);
  const [currentStageId, setCurrentStageId] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [showObjections, setShowObjections] = useState(false);

  const scriptName = leadInfo?.scriptName || 'Live Call Script';
  const structure = leadInfo?.scriptStructure as Record<string, any> | null | undefined;
  const scriptContent = leadInfo?.scriptContent || '';
  const runtimeConfig = structure?.runtimeConfig || {
    openOn: 'connected',
    popupPosition: 'left-bottom',
    mode: 'read-only',
  };

  const objections = useMemo<Objection[]>(() => {
    if (!Array.isArray(structure?.objections)) return [];
    return structure!.objections
      .map((item: any, index: number) => ({
        id: String(item?.id || `obj-${index + 1}`),
        objection: String(item?.objection || ''),
        response: String(item?.response || ''),
      }))
      .filter((item: Objection) => item.objection.trim().length > 0 || item.response.trim().length > 0);
  }, [structure]);

  const runtimeRules = useMemo<RuntimeRule[]>(() => {
    if (!Array.isArray(structure?.runtimeRules)) return [];
    return structure.runtimeRules
      .map((rule: any, idx: number) => ({
        id: String(rule?.id || `rule-${idx + 1}`),
        event: rule?.event,
        sourceStageId: rule?.sourceStageId ? String(rule.sourceStageId) : undefined,
        buttonLabel: rule?.buttonLabel ? String(rule.buttonLabel) : undefined,
        action: rule?.action,
        targetStageId: rule?.targetStageId ? String(rule.targetStageId) : undefined,
        enabled: rule?.enabled !== false,
      }))
      .filter((rule) => !!rule.event && !!rule.action);
  }, [structure]);

  const stages = useMemo<Stage[]>(() => {
    if (structure?.stages && Array.isArray(structure.stages) && structure.stages.length > 0) {
      const rawStages = structure.stages
        .map((stage: any, index: number) => ({
          id: String(stage?.id || `stage-${index + 1}`),
          title: String(stage?.title || `Stage ${index + 1}`),
          body: String(stage?.body || ''),
          choices: Array.isArray(stage?.choices)
            ? stage.choices.map((c: any, ci: number) => ({
                id: String(c?.id || `choice-${index}-${ci}`),
                label: String(c?.label || 'Next'),
                nextStageId: String(c?.nextStageId || ''),
                color: c?.color || undefined,
              }))
            : [],
          notes: {
            goal: String(stage?.notes?.goal || ''),
            avoid: String(stage?.notes?.avoid || ''),
            handle: String(stage?.notes?.handle || ''),
            psychology: String(stage?.notes?.psychology || ''),
          },
        }))
        .filter((stage: Stage) => stage.body.trim().length > 0);

      const stageIds = rawStages.map((s: Stage) => s.id);
      return rawStages.map((stage: Stage) => ({
        ...stage,
        choices:
          stage.choices.length > 0
            ? stage.choices
            : buildDefaultChoices(stage.id, stageIds, objections.length > 0),
      }));
    }

    const generated: Stage[] = [];
    if (structure?.opening) {
      generated.push({ id: 'opening', title: 'Opening', body: String(structure.opening), choices: [] });
    }
    if (normalizeArray(structure?.qualification).length) {
      generated.push({
        id: 'qualification',
        title: 'Qualification',
        body: normalizeArray(structure?.qualification).map((q) => `• ${q}`).join('\n'),
        choices: [],
      });
    }
    if (normalizeArray(structure?.valuePitch).length) {
      generated.push({
        id: 'value-pitch',
        title: 'Value Pitch',
        body: normalizeArray(structure?.valuePitch).map((q) => `• ${q}`).join('\n'),
        choices: [],
      });
    }
    if (structure?.close) {
      generated.push({ id: 'close', title: 'Close', body: String(structure.close), choices: [] });
    }
    if (structure?.followUp) {
      generated.push({ id: 'follow-up', title: 'Follow Up', body: String(structure.followUp), choices: [] });
    }
    if (normalizeArray(structure?.compliance).length) {
      generated.push({
        id: 'compliance',
        title: 'Compliance',
        body: normalizeArray(structure?.compliance).map((q) => `⚠ ${q}`).join('\n'),
        choices: [],
      });
    }

    if (generated.length > 0) {
      const ids = generated.map((s) => s.id);
      return generated.map((stage) => ({
        ...stage,
        choices: buildDefaultChoices(stage.id, ids, objections.length > 0),
      }));
    }

    if (scriptContent.trim()) {
      return [{ id: 'live-script', title: 'Live Script', body: scriptContent, choices: [] }];
    }
    return [];
  }, [structure, scriptContent, objections.length]);

  const activeStage = useMemo(() => {
    if (!stages.length) return null;
    if (currentStageId) {
      const found = stages.find((s) => s.id === currentStageId);
      if (found) return found;
    }
    return stages[0];
  }, [stages, currentStageId]);

  const stageIndex = activeStage ? stages.findIndex((s) => s.id === activeStage.id) : 0;

  const navigateTo = useCallback(
    (targetId: string) => {
      if (targetId === '__objections__') {
        setShowObjections(true);
        return;
      }
      setShowObjections(false);
      setHistory((prev) => {
        const cur = currentStageId || stages[0]?.id;
        if (cur && cur !== targetId) return [...prev, cur];
        return prev;
      });
      setCurrentStageId(targetId);
    },
    [currentStageId, stages]
  );

  const applyRuntimeRules = useCallback(
    (
      event: RuntimeRule['event'],
      payload?: { stageId?: string; buttonLabel?: string; defaultNextStageId?: string },
    ) => {
      const matching = runtimeRules.filter((rule) => {
        if (!rule.enabled) return false;
        if (rule.event !== event) return false;
        if (event === 'stage_enter' && rule.sourceStageId && rule.sourceStageId !== payload?.stageId) return false;
        if (event === 'button_click' && rule.buttonLabel && rule.buttonLabel !== payload?.buttonLabel) return false;
        return true;
      });
      if (!matching.length) return false;

      let handled = false;
      for (const rule of matching) {
        if (rule.action === 'go_to_stage' && rule.targetStageId) {
          navigateTo(rule.targetStageId);
          handled = true;
          continue;
        }
        if (rule.action === 'open_objections') {
          setShowObjections(true);
          handled = true;
          continue;
        }
        if (rule.action === 'restart_script') {
          setCurrentStageId(stages[0]?.id || null);
          setHistory([]);
          setShowObjections(false);
          handled = true;
        }
      }
      return handled;
    },
    [navigateTo, runtimeRules, stages],
  );

  const goBack = useCallback(() => {
    if (showObjections) {
      setShowObjections(false);
      return;
    }
    setHistory((prev) => {
      if (prev.length === 0) return prev;
      const newHistory = [...prev];
      const prevId = newHistory.pop()!;
      setCurrentStageId(prevId);
      return newHistory;
    });
  }, [showObjections]);

  const restart = useCallback(() => {
    setCurrentStageId(stages[0]?.id || null);
    setHistory([]);
    setShowObjections(false);
  }, [stages]);

  useEffect(() => {
    if (!isActive) return;
    setMinimized(false);
    setCurrentStageId(stages[0]?.id || null);
    setHistory([]);
    setShowObjections(false);
  }, [isActive, leadInfo?.callAttemptId, stages]);

  useEffect(() => {
    if (!isActive || callStatus !== 'in-call') return;
    applyRuntimeRules('connected', { stageId: stages[0]?.id });
  }, [isActive, callStatus, applyRuntimeRules, stages]);

  useEffect(() => {
    if (!activeStage?.id) return;
    applyRuntimeRules('stage_enter', { stageId: activeStage.id });
  }, [activeStage?.id, applyRuntimeRules]);

  const shouldOpenOnConnected = runtimeConfig.openOn === 'connected';
  const isConnected = isActive && callStatus === 'in-call';
  if (!shouldOpenOnConnected || !isConnected || stages.length === 0) return null;

  const handleChoiceClick = (choice: StageChoice) => {
    const handled = applyRuntimeRules('button_click', {
      stageId: activeStage?.id,
      buttonLabel: choice.label,
      defaultNextStageId: choice.nextStageId,
    });
    if (!handled) {
      navigateTo(choice.nextStageId);
    }
  };

  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        className="fixed bottom-6 left-6 z-[9998] flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/40 hover:from-blue-500 hover:to-indigo-500 transition-all"
      >
        <SparklesIcon className="h-4 w-4" />
        Script Assist
      </button>
    );
  }

  const canGoBack = showObjections || history.length > 0;
  const progressPct = stages.length > 1 ? ((stageIndex + 1) / stages.length) * 100 : 100;

  return (
    <div className="fixed left-6 bottom-6 z-[9998] flex w-[860px] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-2xl border border-slate-500/70 bg-slate-900 text-slate-100 shadow-2xl shadow-black/40">
      {/* Header */}
      <div className="flex items-center justify-between bg-gradient-to-r from-blue-800/90 to-indigo-800/90 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <SparklesIcon className="h-4 w-4 flex-shrink-0 text-blue-300" />
            <span className="truncate">{scriptName}</span>
            <span className="ml-2 rounded-full bg-emerald-500/25 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-300">
              Connected
            </span>
          </div>
          {!showObjections && activeStage && (
            <p className="mt-0.5 text-xs text-blue-200/70">
              Step {stageIndex + 1} of {stages.length} &middot; {activeStage.title}
            </p>
          )}
          {showObjections && (
            <p className="mt-0.5 text-xs text-amber-300/80">Objection Handling</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={restart}
            className="rounded-md p-1.5 text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
            title="Restart script"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setMinimized(true)}
            className="rounded-md p-1.5 text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
            title="Minimize"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {!showObjections && stages.length > 1 && (
        <div className="h-1 w-full bg-slate-700/80">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      )}

      {/* Body */}
      <div className="grid gap-0 border-t border-slate-700/50 lg:grid-cols-[1fr_320px]">
        <div className="min-h-[360px] border-r border-slate-700/50">
          <div className="max-h-[360px] overflow-y-auto px-4 py-3">
            {showObjections ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-400" />
                  <h3 className="text-sm font-semibold text-amber-300">Objection Handling</h3>
                </div>
                {objections.length === 0 ? (
                  <p className="text-sm text-slate-400">No objection responses configured.</p>
                ) : (
                  objections.map((item) => (
                    <div key={item.id} className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
                      <p className="text-xs font-bold text-amber-300 leading-snug">&ldquo;{item.objection}&rdquo;</p>
                      <p className="mt-1.5 whitespace-pre-wrap text-xs leading-relaxed text-slate-200">
                        {item.response}
                      </p>
                    </div>
                  ))
                )}
              </div>
            ) : activeStage ? (
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-blue-400">
                  Agent Script
                </div>
                <h3 className="mb-3 text-xl font-bold text-blue-300">{activeStage.title}</h3>
                <div className="whitespace-pre-wrap rounded-2xl border border-slate-700 bg-slate-800/55 p-4 text-sm leading-relaxed text-slate-100">
                  {activeStage.body}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="max-h-[360px] overflow-y-auto bg-slate-800/45 px-4 py-3">
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-blue-400">
            Rules / Importance
          </div>
          {activeStage ? (
            <div className="space-y-2.5">
              <div className="rounded-xl border border-slate-600 bg-slate-800/70 p-3">
                <p className="mb-1 text-xs font-bold text-slate-100">Goal of this stage</p>
                <ul className="list-disc space-y-0.5 pl-4 text-xs text-slate-300">
                  {(bulletLines(activeStage.notes?.goal || '').length
                    ? bulletLines(activeStage.notes?.goal || '')
                    : ['Keep tone natural and move to the next step clearly.']
                  ).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-red-900/40 bg-red-950/30 p-3">
                <p className="mb-1 text-xs font-bold text-red-300">What to avoid</p>
                <ul className="list-disc space-y-0.5 pl-4 text-xs text-red-100/90">
                  {(bulletLines(activeStage.notes?.avoid || '').length
                    ? bulletLines(activeStage.notes?.avoid || '')
                    : ['Avoid sounding rushed or robotic.']
                  ).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-emerald-900/40 bg-emerald-950/30 p-3">
                <p className="mb-1 text-xs font-bold text-emerald-300">How to handle</p>
                <ul className="list-disc space-y-0.5 pl-4 text-xs text-emerald-100/90">
                  {(bulletLines(activeStage.notes?.handle || '').length
                    ? bulletLines(activeStage.notes?.handle || '')
                    : ['Use concise prompts and pause after key questions.']
                  ).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-blue-900/40 bg-blue-950/30 p-3">
                <p className="mb-1 text-xs font-bold text-blue-300">Psychology</p>
                <ul className="list-disc space-y-0.5 pl-4 text-xs text-blue-100/90">
                  {(bulletLines(activeStage.notes?.psychology || '').length
                    ? bulletLines(activeStage.notes?.psychology || '')
                    : ['Clear structure lowers friction and improves response quality.']
                  ).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-400">No stage selected.</p>
          )}
        </div>
      </div>

      {/* Choice buttons */}
      <div className="border-t border-slate-700/50 bg-slate-800/45 px-4 py-3">
        {showObjections ? (
          <button
            type="button"
            onClick={goBack}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-slate-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-600 transition-colors"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Back to Script
          </button>
        ) : (
          <div className="space-y-2">
            {activeStage && activeStage.choices.length > 0 && (
              <div className={`grid gap-2 ${activeStage.choices.length === 1 ? 'grid-cols-1' : activeStage.choices.length === 2 ? 'grid-cols-2' : 'grid-cols-2'}`}>
                {activeStage.choices.map((choice) => (
                  <button
                    key={choice.id}
                    type="button"
                    onClick={() => handleChoiceClick(choice)}
                    className={`rounded-lg px-3 py-2.5 text-sm font-medium transition-all active:scale-[0.98] ${
                      choice.color && CHOICE_COLORS[choice.color]
                        ? CHOICE_COLORS[choice.color]
                        : DEFAULT_CHOICE_COLOR
                    }`}
                  >
                    {choice.label}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              {canGoBack && (
                <button
                  type="button"
                  onClick={goBack}
                  className="flex items-center gap-1 rounded-lg bg-slate-800 px-3 py-2 text-xs font-medium text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                >
                  <ChevronLeftIcon className="h-3.5 w-3.5" />
                  Back
                </button>
              )}
              {stageIndex < stages.length - 1 && !activeStage?.choices.some((c) => c.nextStageId === stages[stageIndex + 1]?.id) && (
                <button
                  type="button"
                  onClick={() => navigateTo(stages[stageIndex + 1].id)}
                  className="ml-auto flex items-center gap-1 rounded-lg bg-slate-800 px-3 py-2 text-xs font-medium text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                >
                  Skip
                  <ChevronRightIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScriptAssistPopup;
